import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from './components/Layout';
import AuthGuard from './components/AuthGuard';
import Landing from './pages/Landing';
import Auth from './pages/Auth';
import Home from './pages/Home';
import Document from './pages/Document';
import Settings from './pages/Settings';
import Tasks from './pages/Tasks';
import { useUIStore, useAuthStore } from './lib/store';

export default function App() {
  const { i18n } = useTranslation();
  const { theme } = useUIStore();
  const { isAuthenticated } = useAuthStore();

  // Handle RTL direction
  useEffect(() => {
    const isRtl = i18n.language === 'ar';
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Handle theme
  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      root.classList.toggle('dark', mediaQuery.matches);
      
      const handleChange = (e: MediaQueryListEvent) => {
        root.classList.toggle('dark', e.matches);
      };
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      root.classList.toggle('dark', theme === 'dark');
    }
  }, [theme]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={isAuthenticated ? <Navigate to="/app" replace /> : <Landing />} />
      <Route path="/login" element={isAuthenticated ? <Navigate to="/app" replace /> : <Auth mode="login" />} />
      <Route path="/register" element={isAuthenticated ? <Navigate to="/app" replace /> : <Auth mode="register" />} />
      
      {/* Protected app routes */}
      <Route
        path="/app"
        element={
          <AuthGuard>
            <Layout />
          </AuthGuard>
        }
      >
        <Route index element={<Home />} />
        <Route path="document/:id" element={<Document />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

